import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { PublicFaqGroup, fetchPublicFaq } from '@/services/faq';

function SectionHeader({ label, title, subtitle }: { label: string; title: string; subtitle?: string }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div ref={ref} className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} text-center mb-8 md:mb-12`}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-sparkling-line text-[#1B3D2A]" />
          </div>
        </div>
        <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">{label}</span>
      </div>
      <h2 className="text-2xl md:text-3xl font-bold text-[#1B3D2A]">{title}</h2>
      {subtitle && <p className="mt-3 text-sm text-[#5A6B5A]">{subtitle}</p>}
      <div className="w-12 h-1 bg-[#D4A574] mx-auto mt-3 rounded-full" />
    </div>
  );
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  const delayClass = delay === 0 ? '' : delay === 1 ? 'scroll-reveal-delay-1' : delay === 2 ? 'scroll-reveal-delay-2' : delay === 3 ? 'scroll-reveal-delay-3' : delay === 4 ? 'scroll-reveal-delay-4' : delay === 5 ? 'scroll-reveal-delay-5' : 'scroll-reveal-delay-6';
  return (
    <div ref={ref} className={`scroll-reveal ${delayClass} ${isVisible ? 'scroll-reveal-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

function buildFaqSchema(faqGroups: PublicFaqGroup[]) {
  const mainEntity = faqGroups.flatMap((group) =>
    group.items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  );

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

export default function FaqPage() {
  const navigate = useNavigate();
  const [faqGroups, setFaqGroups] = useState<PublicFaqGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPublicFaq()
      .then((groups) => {
        if (!alive) return;
        setFaqGroups(groups);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const toggleQuestion = (key: string) => {
    setOpenQuestions((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const faqSchema = buildFaqSchema(faqGroups);
  const totalCategories = faqGroups.length;
  const totalQuestions = faqGroups.reduce((sum, group) => sum + group.items.length, 0);

  return (
    <div className="min-h-screen bg-white">
      {/* Schema.org JSON-LD FAQPage */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      <Navbar />

      <main className="pt-14 md:pt-20">
        {/* ===== Hero ===== */}
        <section className="relative text-white pt-8 md:pt-14 pb-8 md:pb-14">
          {/* Decorative Layer (clipped) */}
          <div className="absolute inset-0 overflow-hidden">
            {/* Solid Dark Gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#0F2A1C] via-[#1B3D2A] to-[#0F2A1C]" />
            {/* Background Image - subtle texture only */}
            <div className="absolute inset-0 opacity-[0.06]">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20inviting%20modern%20consultation%20room%20with%20soft%20lighting%20comfortable%20chairs%20and%20green%20botanical%20accents%20professional%20healthcare%20setting%20calm%20reassuring%20atmosphere%20minimalist%20interior%20design%20sage%20tones&width=1600&height=700&seq=faq-hero-2026&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            {/* Subtle Geometric Pattern */}
            <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_20%_30%,_#D4A574_1px,_transparent_1px),radial-gradient(circle_at_80%_70%,_#D4A574_1px,_transparent_1px)] bg-[length:60px_60px,80px_80px]" />
            {/* Decorative Glow */}
            <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#D4A574]/8 blur-[120px]" />
            <div className="absolute -bottom-40 -left-20 w-[500px] h-[500px] rounded-full bg-[#2E8B57]/12 blur-[100px]" />
          </div>

          <div className="relative px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 md:gap-8">
              {/* Left: Title & Description */}
              <div className="max-w-2xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-px h-5 bg-[#D4A574]" />
                  <span className="text-xs font-semibold tracking-[0.2em] text-[#D4A574] uppercase">
                    Frequently Asked Questions
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.25] md:leading-[1.2]">
                  聽力常見
                  <span className="block text-[#D4A574] mt-2 md:mt-3">問題</span>
                </h1>
                <p className="mt-3 md:mt-4 text-white/88 text-base md:text-lg leading-relaxed max-w-xl">
                  彙整聽力保健、助聽器選配、聽力檢測及聽力與慢性病相關的常見問題，
                  由富聆聽專業聽力師團隊為您解答，讓您一次掌握聽力健康關鍵資訊。
                </p>
                {/* FAQ stats */}
                <div className="mt-4 md:mt-5 flex flex-wrap items-center gap-6">
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-question-answer-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">{totalCategories} 大主題分類</span>
                  </div>
                  <div className="flex items-center gap-2 text-white/80 text-xs md:text-sm">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-[#D4A574] text-xs" />
                    </div>
                    <span className="whitespace-nowrap">{totalQuestions} 則常見問答</span>
                  </div>
                </div>
              </div>

              {/* Right: Search hint */}
              <div className="flex items-center gap-3 md:gap-4 shrink-0">
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-search-line text-[#D4A574]" />
                    </div>
                  </div>
                  <span className="text-xs text-white/70 whitespace-nowrap">快速搜尋解答</span>
                </div>
                <div className="flex flex-col items-center bg-white/8 backdrop-blur-sm rounded-2xl border border-white/10 px-4 py-3 md:px-5 md:py-3.5">
                  <div className="w-8 h-8 rounded-full bg-[#D4A574]/20 flex items-center justify-center mb-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-customer-service-2-line text-[#D4A574]" />
                    </div>
                  </div>
                  <span className="text-xs text-white/70 whitespace-nowrap">專人諮詢服務</span>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Wave */}
          <svg className="absolute bottom-[-1px] left-0 w-full h-auto block" viewBox="0 0 1440 100" fill="none" preserveAspectRatio="none">
            <path d="M0 100L60 90C120 80 240 60 360 50C480 40 600 38 720 44C840 50 960 62 1080 68C1200 74 1320 74 1380 74L1440 74V100H1380C1320 100 1200 100 1080 100C960 100 840 100 720 100C600 100 480 100 360 100C240 100 120 100 60 100H0Z" fill="white"/>
          </svg>
        </section>

        {/* ===== FAQ Categories ===== */}
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-7xl mx-auto">
          <SectionHeader label="Questions & Answers" title="常見問題分類" subtitle="點擊分類展開，找到您想了解的問題與解答" />

          {/* Category Tabs */}
          {loading ? (
            <div className="py-10 text-center text-[#9A8A7A]">載入中...</div>
          ) : faqGroups.length === 0 ? (
            <div className="py-10 text-center text-[#9A8A7A]">目前尚無常見問題內容。</div>
          ) : (
          <>
          <div className="flex flex-wrap gap-2 mb-8 md:mb-10">
            {faqGroups.map((group) => (
              <button
                type="button"
                key={group.faqCategoryId}
                onClick={() => setOpenCategory(openCategory === group.category ? null : group.category)}
                className={`px-4 md:px-6 py-2.5 md:py-3 rounded-full text-sm md:text-base font-medium transition-all whitespace-nowrap border flex items-center gap-2 ${
                  openCategory === group.category
                    ? 'bg-[#1B3D2A] text-white border-[#1B3D2A]'
                    : 'bg-white text-[#5A6B5A] border-[#E8E0D6] hover:border-[#D4A574] hover:text-[#1B3D2A]'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${group.icon} text-sm`} />
                </div>
                {group.category}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-4 md:space-y-6">
            {faqGroups.map((group, groupIdx) => (
              <SectionReveal key={group.faqCategoryId} delay={groupIdx}>
                <div
                  className={`bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden transition-all duration-300 ${
                    openCategory === group.category ? 'border-[#D4A574]/40 shadow-lg' : 'hover:border-[#D4A574]/30'
                  }`}
                >
                  {/* Category Header */}
                  <button
                    type="button"
                    onClick={() => setOpenCategory(openCategory === group.category ? null : group.category)}
                    className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${
                        openCategory === group.category ? 'bg-[#1B3D2A]' : 'bg-[#1B3D2A]/10'
                      }`}>
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${group.icon} text-lg ${openCategory === group.category ? 'text-white' : 'text-[#1B3D2A]'}`} />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-base md:text-lg font-bold text-[#1B3D2A]">{group.category}</h3>
                        <p className="text-sm text-[#9A8A7A]">{group.items.length} 個問題</p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                      openCategory === group.category ? 'bg-[#1B3D2A] text-white rotate-180' : 'bg-[#FAF8F5] text-[#9A8A7A]'
                    }`}>
                      <i className="ri-arrow-down-s-line text-lg" />
                    </div>
                  </button>

                  {/* Questions */}
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openCategory === group.category ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="px-5 md:px-6 pb-5 md:pb-6 space-y-3">
                      {group.items.map((item, qIdx) => {
                        const key = `${group.faqCategoryId}-${item.id}`;
                        const isOpen = openQuestions.has(key);
                        return (
                          <div
                            key={key}
                            className={`rounded-xl border transition-all duration-300 ${
                              isOpen ? 'border-[#D4A574]/40 bg-[#FAF8F5]' : 'border-[#E8E0D6]/60 bg-white'
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() => toggleQuestion(key)}
                              className="w-full flex items-start gap-3 p-4 md:p-5 text-left"
                            >
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-colors duration-300 ${
                                isOpen ? 'bg-[#1B3D2A]' : 'bg-[#1B3D2A]/10'
                              }`}>
                                <span className={`text-xs font-bold ${isOpen ? 'text-white' : 'text-[#1B3D2A]'}`}>
                                  {qIdx + 1}
                                </span>
                              </div>
                              <span className="text-sm md:text-base font-semibold text-[#1B3D2A] leading-snug flex-1">
                                {item.question}
                              </span>
                              <div className={`w-6 h-6 flex items-center justify-center shrink-0 mt-1 transition-transform duration-300 ${
                                isOpen ? 'rotate-180' : ''
                              }`}>
                                <i className="ri-arrow-down-s-line text-[#9A8A7A] text-lg" />
                              </div>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ${
                              isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                            }`}>
                              <div className="px-4 md:px-5 pb-4 md:pb-5 pl-14 md:pl-16">
                                <p className="text-sm md:text-base text-[#5A6B5A] leading-relaxed">
                                  {item.answer}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
          </div>
          </>
          )}
        </div>

        {/* Wave: white to warm */}
        <div className="relative overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 0L48 5C96 10 192 20 288 24C384 28 480 28 576 26C672 24 768 20 864 16C960 12 1056 8 1152 10C1248 12 1344 20 1392 24L1440 28V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0V0Z" fill="#FAF8F5" />
          </svg>
        </div>

        {/* ===== CTA ===== */}
        <div className="bg-[#FAF8F5] py-10 md:py-14">
          <div className="px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
            <SectionReveal>
              <div className="bg-white rounded-2xl border border-[#E8E0D6] p-6 md:p-10 flex flex-col md:flex-row items-stretch md:items-center gap-5 md:gap-8">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-customer-service-2-line text-[#1B3D2A]" />
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">專業諮詢</span>
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#1B3D2A]">
                    還有其他問題？
                  </h3>
                  <p className="mt-3 text-sm text-[#5A6B5A] leading-relaxed">
                    每個人的聽力狀況都不同，我們的專業聽力師樂意為您提供一對一的諮詢服務。歡迎預約免費聽力檢測，或透過聯絡我們獲得更詳細的解答。
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 shrink-0">
                  <button
                    type="button"
                    onClick={() => navigate('/booking')}
                    className="bg-[#1B3D2A] text-white px-6 md:px-8 py-3 rounded-xl text-sm font-bold hover:bg-[#143026] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-calendar-check-line" />
                    </div>
                    預約免費檢測
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/contact')}
                    className="border border-[#E8E0D6] text-[#5A6B5A] px-6 md:px-8 py-3 rounded-xl text-sm font-semibold hover:border-[#1B3D2A] hover:text-[#1B3D2A] transition-colors whitespace-nowrap flex items-center justify-center gap-2 min-h-[52px]"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-mail-line" />
                    </div>
                    聯絡我們
                  </button>
                </div>
              </div>
            </SectionReveal>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}